import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Bell, FileText, MessageSquare, CalendarClock, Activity, ShieldCheck, Send } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import InvestorShell from "./InvestorShell";
import { api, money, Badge } from "./api";

const ACCREDITED = ["Yes — self-reported", "No", "Not sure"];
const INVESTOR_TYPES = ["Individual", "Entity / LLC", "Trust", "Self-directed IRA", "Other"];

export default function InvestorDashboard() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const load = useCallback(() => api.get("/investor/me").then(({ data }) => setMe(data)).catch(() => setMe({ error: true })), []);
  useEffect(() => { load(); }, [load]);

  if (!me) return <Center><Loader2 className="animate-spin text-red-700" /></Center>;

  const reqStatus = me.access_request?.status;
  let body;
  if (!me.profile && !reqStatus) body = <NoRequest />;
  else if (!me.profile && (reqStatus === "new" || reqStatus === "reviewing")) body = <Pending status={reqStatus} />;
  else if (!me.profile && reqStatus === "declined") body = <Declined />;
  else if (!me.profile && reqStatus === "approved") body = <ProfileForm onDone={load} />;
  else body = <Dashboard me={me} reload={load} />;

  return (
    <InvestorShell gates={me.gates} title={me.profile ? `Welcome, ${me.profile.name || user?.name || "Investor"}` : "Investor Portal"}
      subtitle={me.profile ? `${me.profile.stage === "approved" ? "Approved Investor" : "Prospective Investor"} · ${me.profile.email}` : "Access review in progress"}>
      {body}
    </InvestorShell>
  );
}

const Center = ({ children }) => <div className="grid min-h-screen place-items-center bg-ink pt-[92px]">{children}</div>;

function NoRequest() {
  return (
    <div className="card-line mx-auto max-w-lg rounded-2xl bg-white p-10 text-center" data-testid="investor-no-request">
      <ShieldCheck size={34} className="mx-auto text-red-700" />
      <h2 className="mt-4 font-display text-xl font-extrabold text-offwhite">Portal access required</h2>
      <p className="mt-3 text-sm text-muted">The Investor Portal is private. Submit an access request and Xtreme Exotics will review it.</p>
      <Link to="/investors/request-access" className="mt-6 inline-block rounded-full bg-red-700 px-7 py-3 font-display text-sm font-bold uppercase tracking-[0.12em] text-white hover:bg-red-600" data-testid="dash-request-access-btn">Request Access</Link>
    </div>
  );
}

const Pending = ({ status }) => (
  <div className="card-line mx-auto max-w-lg rounded-2xl bg-white p-10 text-center" data-testid="investor-pending">
    <Badge value={status} testid="request-status-badge" />
    <h2 className="mt-4 font-display text-xl font-extrabold text-offwhite">Your access request is under review</h2>
    <p className="mt-3 text-sm text-muted">Xtreme Exotics reviews every request. You'll receive an email when a decision is made.</p>
  </div>
);

const Declined = () => (
  <div className="card-line mx-auto max-w-lg rounded-2xl bg-white p-10 text-center" data-testid="investor-declined">
    <h2 className="font-display text-xl font-extrabold text-offwhite">Access not approved</h2>
    <p className="mt-3 text-sm text-muted">This request was not approved. Contact Xtreme Exotics with any questions.</p>
  </div>
);

function ProfileForm({ onDone }) {
  const [form, setForm] = useState({ investor_type: "", entity_name: "", address: "", accredited_status: "", experience: "", ack_confidentiality: false, ack_preliminary: false, ack_electronic: false });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.investor_type && form.address.trim() && form.accredited_status && form.experience.trim()
    && form.ack_confidentiality && form.ack_preliminary && form.ack_electronic;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/investor/profile", form);
      toast.success("Investor profile completed");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save profile");
    } finally { setBusy(false); }
  };
  return (
    <div className="card-line mx-auto max-w-xl rounded-2xl bg-white p-8" data-testid="investor-profile-form">
      <h2 className="font-display text-xl font-extrabold text-offwhite">Complete your investor profile</h2>
      <p className="mt-2 text-sm text-muted">Your access was approved. One more step before reviewing preliminary materials.</p>
      <div className="mt-6 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="field-label" htmlFor="ip-type">Investor type</label>
            <select id="ip-type" className="field" value={form.investor_type} onChange={(e) => set("investor_type", e.target.value)} data-testid="ip-type">
              <option value="">Select…</option>{INVESTOR_TYPES.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label" htmlFor="ip-accredited">Accredited status</label>
            <select id="ip-accredited" className="field" value={form.accredited_status} onChange={(e) => set("accredited_status", e.target.value)} data-testid="ip-accredited">
              <option value="">Select…</option>{ACCREDITED.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="field-label" htmlFor="ip-entity">Entity name (if applicable)</label>
          <input id="ip-entity" className="field" value={form.entity_name} onChange={(e) => set("entity_name", e.target.value)} data-testid="ip-entity" />
        </div>
        <div>
          <label className="field-label" htmlFor="ip-address">Mailing address</label>
          <input id="ip-address" className="field" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="ip-address" />
        </div>
        <div>
          <label className="field-label" htmlFor="ip-experience">Investment experience</label>
          <textarea id="ip-experience" className="field min-h-[80px]" value={form.experience} onChange={(e) => set("experience", e.target.value)} data-testid="ip-experience" />
        </div>
        <div className="space-y-3 rounded-xl border border-black/[0.08] bg-surface/50 p-5">
          {[["ack_confidentiality", "I acknowledge these are confidential preliminary discussion materials; no offer to sell or solicitation to buy securities is being made."],
            ["ack_preliminary", "I understand all figures are illustrative only, and no funds are being accepted."],
            ["ack_electronic", "I consent to electronic communications and electronic delivery of documents and statements."]].map(([k, label]) => (
            <label key={k} className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-muted">
              <input type="checkbox" className="mt-0.5 h-4 w-4 accent-red-700" checked={form[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`ip-${k}`} />
              {label}
            </label>
          ))}
        </div>
        <button onClick={submit} disabled={!valid || busy} className="w-full rounded-full bg-red-700 px-7 py-3.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-white hover:bg-red-600 disabled:opacity-50" data-testid="ip-submit-btn">
          {busy ? <Loader2 size={15} className="mx-auto animate-spin" /> : "Complete Profile & Enter Portal"}
        </button>
      </div>
    </div>
  );
}

function Dashboard({ me, reload }) {
  const [vehicles, setVehicles] = useState(null);
  const [interests, setInterests] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [activity, setActivity] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [q, setQ] = useState({ subject: "", message: "" });
  const [m, setM] = useState({ topic: "", preferred_times: "", note: "" });

  const load = useCallback(() => {
    api.get("/investor/vehicles").then(({ data }) => setVehicles(data)).catch(() => setVehicles([]));
    api.get("/investor/interests").then(({ data }) => setInterests(data)).catch(() => setInterests([]));
    api.get("/investor/notifications").then(({ data }) => setNotifications(data)).catch(() => {});
    api.get("/investor/activity").then(({ data }) => setActivity(data)).catch(() => {});
    api.get("/investor/questions").then(({ data }) => setQuestions(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const indicate = async (vehicle_id, tier_pct) => {
    if (!window.confirm(`Record non-binding interest at the proposed ${tier_pct}% tier? This is non-binding and creates no obligation. Xtreme Exotics will be notified.`)) return;
    try {
      await api.post("/investor/interests", { vehicle_id, tier_pct });
      toast.success("Non-binding interest recorded");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record interest"); }
  };

  const withdraw = async (id) => {
    try { await api.delete(`/investor/interests/${id}`); toast.success("Interest withdrawn"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not withdraw"); }
  };

  const simulate = async (id) => {
    try {
      const { data } = await api.post(`/investor/interests/${id}/simulate-funding`);
      toast.success(`Simulated funding recorded — ${data.label}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Funding simulation unavailable"); }
  };

  const sendQuestion = async () => {
    try { await api.post("/investor/questions", q); toast.success("Question sent"); setQ({ subject: "", message: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
  };

  const sendMeeting = async () => {
    try { await api.post("/investor/meeting-requests", m); toast.success("Meeting requested"); setM({ topic: "", preferred_times: "", note: "" }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not request meeting"); }
  };

  const markRead = async (id) => { await api.post(`/investor/notifications/${id}/read`); load(); reload(); };

  const activeInterests = (interests || []).filter((i) => i.status === "active");
  const isTest = me.gates?.payments_gate === "TEST";

  return (
    <div className="grid gap-6 lg:grid-cols-3" data-testid="investor-dashboard">
      {/* LEFT — main column */}
      <div className="space-y-6 lg:col-span-2">
        {/* status panel */}
        <div className="card-line rounded-xl bg-white p-6" data-testid="panel-status">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="eyebrow !text-red-700">Account status</p>
              <p className="mt-2 font-display text-lg font-extrabold text-offwhite">{me.stage === "approved" ? "Approved Investor" : "Prospective Investor"}</p>
            </div>
            <Badge value={me.stage === "approved" ? "approved" : "reviewing"} testid="stage-badge" />
          </div>
          <p className="mt-3 text-xs leading-relaxed text-muted">
            {me.stage === "approved"
              ? "You can view assigned vehicles, statements, distributions and all approved documents."
              : "You can review approved preliminary materials and submit non-binding interest. Xtreme Exotics may upgrade your access after review."}
          </p>
        </div>

        {/* opportunity cards */}
        <div data-testid="panel-opportunities">
          <h2 className="mb-3 font-display text-base font-extrabold text-offwhite">Vehicle opportunities <span className="ml-1 font-mono text-[10px] uppercase tracking-widest text-muted">Proposed</span></h2>
          {!vehicles && <p className="text-sm text-muted">Loading…</p>}
          <div className="grid gap-4 sm:grid-cols-2">
            {vehicles?.map((v) => (
              <div key={v.vehicle_id} className="card-line rounded-xl bg-white p-5" data-testid={`opp-${v.vehicle_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <Link to={`/investors/portal/vehicles/${v.vehicle_id}`} className="font-display text-sm font-extrabold text-offwhite hover:text-red-700" data-testid={`opp-link-${v.vehicle_id}`}>{v.name}</Link>
                  <Badge value={v.availability} testid={`opp-availability-${v.vehicle_id}`} />
                </div>
                <p className="mt-1 font-mono text-xs text-muted">{money(v.acquisition_cost)} · {v.milestones_complete}/{v.milestones_total} milestones</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {v.tiers.map((t) => (
                    <button key={t.funding_pct} onClick={() => indicate(v.vehicle_id, t.funding_pct)} disabled={v.availability !== "open"}
                      data-testid={`interest-btn-${v.vehicle_id}-${t.funding_pct}`}
                      className="rounded-full border border-red-700/30 px-3 py-1 text-[11px] font-bold text-red-700 transition-colors hover:bg-red-700 hover:text-white disabled:opacity-40">
                      {t.funding_pct}% · {money(t.amount)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* saved vehicles / interests */}
        <div className="card-line rounded-xl bg-white p-6" data-testid="panel-interests">
          <h2 className="font-display text-base font-extrabold text-offwhite">Your non-binding interests</h2>
          {!activeInterests.length && <p className="mt-3 text-sm text-muted">None yet — select a vehicle tier above. Interest is non-binding and creates no obligation.</p>}
          <div className="mt-4 space-y-2">
            {activeInterests.map((i) => (
              <div key={i.interest_id} className="flex flex-wrap items-center gap-3 rounded-lg border border-black/[0.08] p-3" data-testid={`interest-${i.interest_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-offwhite">{i.vehicle_name} — {i.tier_pct}% tier · {money(i.amount)} <span className="font-mono text-[10px] uppercase text-muted">Proposed · Non-binding</span></p>
                  <p className="text-[11px] text-muted">{i.profit_pct}% of Defined Vehicle Profit · submitted {new Date(i.created_at).toLocaleDateString()}</p>
                </div>
                {isTest && (
                  <button onClick={() => simulate(i.interest_id)} className="rounded-full border border-yellow-600/40 bg-yellow-400/10 px-3 py-1.5 text-[11px] font-bold text-yellow-700 hover:bg-yellow-400/20" data-testid={`simulate-funding-${i.interest_id}`}>
                    Simulate Funding — TEST MODE
                  </button>
                )}
                <button onClick={() => withdraw(i.interest_id)} className="text-[11px] font-semibold text-muted hover:text-red-700" data-testid={`withdraw-${i.interest_id}`}>Withdraw</button>
              </div>
            ))}
          </div>
          {isTest && <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-yellow-700">TEST MODE — NO REAL FUNDS are accepted or transferred</p>}
        </div>

        {/* questions center */}
        <div className="card-line rounded-xl bg-white p-6" data-testid="panel-questions">
          <h2 className="flex items-center gap-2 font-display text-base font-extrabold text-offwhite"><MessageSquare size={15} className="text-red-700" /> Questions</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_2fr_auto]">
            <input className="field !py-2.5" placeholder="Subject" value={q.subject} onChange={(e) => setQ({ ...q, subject: e.target.value })} data-testid="question-subject" />
            <input className="field !py-2.5" placeholder="Your question" value={q.message} onChange={(e) => setQ({ ...q, message: e.target.value })} data-testid="question-message" />
            <button onClick={sendQuestion} disabled={!q.subject.trim() || !q.message.trim()} className="btn-ghost !px-5 !py-2.5 !text-xs disabled:opacity-50" data-testid="question-send-btn"><Send size={13} /> Send</button>
          </div>
          <div className="mt-4 space-y-2">
            {questions.map((item) => (
              <div key={item.question_id} className="rounded-lg border border-black/[0.08] p-3" data-testid={`question-${item.question_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-bold text-offwhite">{item.subject}</p>
                  <Badge value={item.status} testid={`question-status-${item.question_id}`} />
                </div>
                <p className="mt-1 text-xs text-muted">{item.message}</p>
                {item.reply && <p className="mt-2 rounded bg-surface/70 p-2 text-xs text-offwhite"><b>XE:</b> {item.reply}</p>}
              </div>
            ))}
          </div>
        </div>

        {/* activity history */}
        <div className="card-line rounded-xl bg-white p-6" data-testid="panel-activity">
          <h2 className="flex items-center gap-2 font-display text-base font-extrabold text-offwhite"><Activity size={15} className="text-red-700" /> Activity history</h2>
          <div className="mt-4 space-y-1.5">
            {!activity.length && <p className="text-sm text-muted">No activity yet.</p>}
            {activity.slice(0, 12).map((a) => (
              <p key={a.event_id} className="flex justify-between gap-3 text-xs text-muted">
                <span className="font-semibold text-offwhite">{a.action.replace(/_/g, " ")}</span>
                <span className="font-mono">{new Date(a.created_at).toLocaleString()}</span>
              </p>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT — sidebar */}
      <div className="space-y-6">
        {/* notifications */}
        <div className="card-line rounded-xl bg-white p-5" data-testid="panel-notifications">
          <h2 className="flex items-center gap-2 font-display text-sm font-extrabold text-offwhite"><Bell size={14} className="text-red-700" /> Notifications</h2>
          <div className="mt-3 space-y-2">
            {!notifications.length && <p className="text-xs text-muted">You're all caught up.</p>}
            {notifications.slice(0, 6).map((n) => (
              <button key={n.notification_id} onClick={() => markRead(n.notification_id)}
                className={`block w-full rounded-lg border p-3 text-left transition-colors ${n.read ? "border-black/[0.06] opacity-60" : "border-red-700/25 bg-red-500/[0.04]"}`}
                data-testid={`notification-${n.notification_id}`}>
                <p className="text-xs font-bold text-offwhite">{n.title}</p>
                <p className="mt-0.5 text-[11px] leading-snug text-muted">{n.body}</p>
              </button>
            ))}
          </div>
        </div>

        {/* document center preview */}
        <div className="card-line rounded-xl bg-white p-5" data-testid="panel-documents">
          <h2 className="flex items-center gap-2 font-display text-sm font-extrabold text-offwhite"><FileText size={14} className="text-red-700" /> Document center</h2>
          <p className="mt-2 text-xs leading-relaxed text-muted">Due-diligence data room with versioned documents and access logging.</p>
          <Link to="/investors/portal/documents" className="btn-ghost mt-3 w-full !py-2 !text-xs" data-testid="open-dataroom-btn">Open Data Room</Link>
        </div>

        {/* diligence checklist */}
        <div className="card-line rounded-xl bg-white p-5" data-testid="panel-diligence">
          <h2 className="font-display text-sm font-extrabold text-offwhite">Due diligence checklist</h2>
          <ul className="mt-3 space-y-2 text-xs text-muted">
            {[
              ["Access request approved", true],
              ["Investor profile & acknowledgements complete", true],
              ["Review preliminary materials", (activity || []).some((a) => a.action === "document_viewed")],
              ["Submit non-binding interest", activeInterests.length > 0],
              ["Due diligence & definitive documents", false],
              ["Funding (after final approval only)", false],
            ].map(([label, done]) => (
              <li key={label} className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${done ? "bg-emerald-500" : "bg-black/15"}`} /> {label}
              </li>
            ))}
          </ul>
        </div>

        {/* meeting request */}
        <div className="card-line rounded-xl bg-white p-5" data-testid="panel-meeting">
          <h2 className="flex items-center gap-2 font-display text-sm font-extrabold text-offwhite"><CalendarClock size={14} className="text-red-700" /> Request a meeting</h2>
          <div className="mt-3 space-y-2.5">
            <input className="field !py-2.5" placeholder="Topic" value={m.topic} onChange={(e) => setM({ ...m, topic: e.target.value })} data-testid="meeting-topic" />
            <input className="field !py-2.5" placeholder="Preferred times (e.g. weekdays after 2pm ET)" value={m.preferred_times} onChange={(e) => setM({ ...m, preferred_times: e.target.value })} data-testid="meeting-times" />
            <button onClick={sendMeeting} disabled={!m.topic.trim() || !m.preferred_times.trim()} className="btn-ghost w-full !py-2 !text-xs disabled:opacity-50" data-testid="meeting-send-btn">Request Meeting</button>
          </div>
        </div>

        {/* acknowledgements record */}
        <div className="card-line rounded-xl bg-white p-5" data-testid="panel-acknowledgements">
          <h2 className="font-display text-sm font-extrabold text-offwhite">Your acknowledgements</h2>
          <ul className="mt-3 space-y-1.5 text-[11px] text-muted">
            {Object.entries(me.profile?.acknowledgements || {}).map(([k, ts]) => (
              <li key={k} className="flex justify-between gap-2">
                <span>{k.replace(/_/g, " ")}</span>
                <span className="font-mono">{new Date(ts).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
